import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { IonosLogo } from '@/modules/user-feedback/components/ionos-logo';
import { Button } from '@/ui/components/ui/button';
import { Card, CardContent } from '@/ui/components/ui/card';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useAdminRegister } from '../hooks/use-admin';

export function AdminSignUpPage() {
	const { user, loading } = useAdminAuth();
	const navigate = useNavigate();
	const [register] = useAdminRegister();

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirm, setConfirm] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	if (!loading && user) {
		return <Navigate to='/admin' replace />;
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (password !== confirm) {
			setError('Passwords do not match');
			return;
		}
		if (password.length < 8) {
			setError('Password must be at least 8 characters');
			return;
		}

		setSubmitting(true);
		try {
			const result = await register({
				variables: {
					input: { email, password, name: name || undefined },
				},
			});
			if (!result.data?.register.success)
				throw new Error('Registration failed');
			navigate('/', {
				replace: true,
				state: { registered: true },
			});
		} catch (err: unknown) {
			const msg =
				err instanceof Error
					? err.message.includes('already exists')
						? 'An account with this email already exists'
						: err.message
					: 'Registration failed';
			setError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className='min-h-screen bg-background flex items-center justify-center px-4'>
			<div className='w-full max-w-sm'>
				<div className='mb-10 flex flex-col items-center gap-4'>
					<IonosLogo />
					<div className='text-center'>
						<h1 className='text-2xl font-semibold text-foreground tracking-tight'>
							Feature Request
						</h1>
						<p className='mt-1 text-sm text-muted-foreground'>
							Register to access the admin panel
						</p>
					</div>
				</div>

				<Card>
					<CardContent className='pt-6'>
						<form
							onSubmit={handleSubmit}
							className='flex flex-col gap-4'
						>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='name'>Name</Label>
								<Input
									id='name'
									type='text'
									autoComplete='name'
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder='Your name'
								/>
							</div>

							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='email'>Email</Label>
								<Input
									id='email'
									type='email'
									autoComplete='email'
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder='admin@example.com'
								/>
							</div>

							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='password'>Password</Label>
								<Input
									id='password'
									type='password'
									autoComplete='new-password'
									required
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
									placeholder='Min. 8 characters'
								/>
							</div>

							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='confirm'>
									Confirm password
								</Label>
								<Input
									id='confirm'
									type='password'
									autoComplete='new-password'
									required
									value={confirm}
									onChange={(e) => setConfirm(e.target.value)}
									placeholder='Repeat your password'
								/>
							</div>

							{error && (
								<p className='text-sm text-destructive rounded-xl bg-destructive/10 px-3.5 py-2.5'>
									{error}
								</p>
							)}

							<Button
								type='submit'
								disabled={submitting}
								className='mt-1 w-full'
							>
								{submitting
									? 'Creating account…'
									: 'Create account'}
							</Button>
						</form>
					</CardContent>
				</Card>

				<p className='mt-4 text-center text-sm text-muted-foreground'>
					Already have an account?{' '}
					<Link
						to='/'
						className='text-primary hover:underline font-medium'
					>
						Sign in
					</Link>
				</p>
			</div>
		</div>
	);
}
